import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleRemarkService } from 'src/app/services/vehicle-remarks/vehicle-remark.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-vh-manager-vehicle-remarks',
  templateUrl: './vh-manager-vehicle-remarks.component.html',
  styleUrls: ['./vh-manager-vehicle-remarks.component.scss']
})
export class VhManagerVehicleRemarksComponent extends DestructorComponent implements OnInit {

  @Output() remarkAdded = new EventEmitter<void>();

  remarkForm: FormGroup;
  isLoading: boolean = false;
  selectedVehicle: any = null;
  vehicleRemarks: any[] = [];
  isLoadingRemarks: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _vehicleRemarkService: VehicleRemarkService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.remarkForm = this.fb.group({
      vehicle_remark: ['', [Validators.required]],
      spare_key_part_info: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vehicle_remarks');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const vehicle = (window as any).selectedVehicleForRemarks;
        if (vehicle) {
          this.selectedVehicle = vehicle;
          this.loadVehicleRemarks();
        } else {
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetForm();
        this.selectedVehicle = null;
        this.vehicleRemarks = [];
      });
    }
  }

  private loadVehicleRemarks(): void {
    if (!this.selectedVehicle?._id) return;

    this.isLoadingRemarks = true;
    this.subscriptions.push(
      this._vehicleRemarkService.GetData('1', '', { vehicle_id: this.selectedVehicle._id, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.vehicleRemarks = response.data.data || response.data || [];
          } else {
            this.vehicleRemarks = [];
          }
          this.isLoadingRemarks = false;
        },
        error: (error) => {
          console.error('Error loading vehicle remarks:', error);
          this.vehicleRemarks = [];
          this.isLoadingRemarks = false;
        }
      })
    );
  }

  onSubmit(): void {
    if (this.remarkForm.valid && this.selectedVehicle) {
      this.isLoading = true;

      const formData = {
        ...this.remarkForm.value,
        vehicle_id: this.selectedVehicle._id
      };

      this.subscriptions.push(
        this._vehicleRemarkService.AddData(formData).subscribe({
          next: (response: any) => {
            if (response.status) {
              Swal.fire({
                title: 'Success!',
                text: 'Vehicle remark added successfully.',
                icon: 'success',
                confirmButtonText: 'OK'
              });
              this.resetForm();
              this.loadVehicleRemarks();
              this.remarkAdded.emit();
            } else {
              Swal.fire({
                title: 'Error!',
                text: response.message || 'Failed to add vehicle remark.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Error adding vehicle remark:', error);
            Swal.fire({
              title: 'Error!',
              text: 'Failed to add vehicle remark. Please try again.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
            this.isLoading = false;
          }
        })
      );
    } else {
      this.markFormGroupTouched();
    }
  }

  deleteRemark(remark: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.subscriptions.push(
          this._vehicleRemarkService.DeleteDataById(remark._id).subscribe({
            next: (response: any) => {
              if (response.status) {
                Swal.fire({
                  title: 'Deleted!',
                  text: 'Vehicle remark has been deleted.',
                  icon: 'success',
                  confirmButtonText: 'OK'
                });
                this.loadVehicleRemarks();
                this.remarkAdded.emit();
              } else {
                Swal.fire({
                  title: 'Error!',
                  text: response.message || 'Failed to delete vehicle remark.',
                  icon: 'error',
                  confirmButtonText: 'OK'
                });
              }
            },
            error: (error) => {
              console.error('Error deleting vehicle remark:', error);
              Swal.fire({
                title: 'Error!',
                text: 'Failed to delete vehicle remark. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
              });
            }
          })
        );
      }
    });
  }

  private resetForm(): void {
    this.remarkForm.reset();
    this.isLoading = false;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.remarkForm.controls).forEach(key => {
      const control = this.remarkForm.get(key);
      control?.markAsTouched();
    });
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vehicle_remarks');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
      this.resetForm();
    }
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Getters for form controls
  get vehicle_remark() { return this.remarkForm.get('vehicle_remark'); }
  get spare_key_part_info() { return this.remarkForm.get('spare_key_part_info'); }
}
